// components/industry-templates/ArtEventTemplate.tsx
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Palette, Image, User, Award } from 'lucide-react';

export const ArtEventTemplate = ({ structure, theme }: any) => {
  const industryData = structure.industry_data;

  return (
    <div className="space-y-8">
      {/* Featured Artists */}
      {industryData?.featured_artists && (
        <Card className="border-0 bg-gradient-to-br from-purple-500/20 to-pink-500/20 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2 text-2xl">
              <Palette className="w-8 h-8" style={{ color: theme.primary }} />
              Featured Artists
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {industryData.featured_artists.map((artist: any, idx: number) => (
                <div key={idx} className="p-5 bg-white/10 rounded-xl border border-white/20">
                  <div className="flex items-start gap-4">
                    <div className="text-4xl">🎨</div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-white mb-1">{artist.name}</h3>
                      <p className="text-slate-300 mb-2">{artist.style}</p>
                      <Badge style={{ backgroundColor: theme.primary }}>
                        {artist.works_displayed} works on display
                      </Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Gallery Spaces */}
      {industryData?.gallery_spaces && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Image className="w-6 h-6" style={{ color: theme.primary }} />
              Gallery Spaces & Exhibitions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-white/5 rounded-lg text-center">
                <p className="text-3xl font-bold" style={{ color: theme.primary }}>{industryData.gallery_spaces}</p>
                <p className="text-sm text-slate-400">Gallery Spaces</p>
              </div>
              <div className="p-4 bg-white/5 rounded-lg text-center">
                <p className="text-3xl font-bold" style={{ color: theme.primary }}>{industryData.artworks_displayed}</p>
                <p className="text-sm text-slate-400">Artworks</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Art Workshops */}
      {industryData?.art_workshops && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white">Live Art Workshops</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {industryData.art_workshops.map((workshop: any, idx: number) => (
                <div key={idx} className="p-4 bg-white/5 rounded-lg border-l-4" style={{ borderColor: theme.primary }}>
                  <h4 className="font-bold text-white">{workshop.title}</h4>
                  <p className="text-sm text-slate-400 mt-1">{workshop.medium}</p>
                  <div className="flex gap-2 mt-2">
                    <Badge variant="outline" className="border-white/20 text-white">{workshop.duration}</Badge>
                    <Badge variant="outline" className="border-white/20 text-white">₹{workshop.price}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Auction */}
      {industryData?.live_auction && (
        <Card className="border-2" style={{ borderColor: theme.primary, backgroundColor: 'rgba(168, 85, 247, 0.1)' }}>
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Award className="w-6 h-6" style={{ color: theme.primary }} />
              Live Art Auction
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-white/5 rounded-lg p-4">
              <p className="text-white text-lg mb-2">Bid on exclusive artworks</p>
              <p className="text-slate-400">Starting bids from ₹{industryData.live_auction.starting_bid}</p>
              {industryData.live_auction.pieces && (
                <p className="text-sm text-slate-500 mt-2">{industryData.live_auction.pieces} pieces available</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
